/**
 * CoachingDrawer — slide-out panel that renders a Lumina CCM coaching
 * report for a single rep.
 *
 * Triggered from the "Coach" button on `/admin/rep-activity` rollup
 * rows (super_admin / crm_admin / manager only). Calls
 * POST /api/admin/coaching/rep/{rep_id}/run on first open and
 * caches the markdown locally for the session.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { X, Sparkles, RefreshCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const MD_COMPONENTS = {
  h2: (p) => <h2 {...p} style={{ fontSize: 17, fontWeight: 700, marginTop: 8, marginBottom: 14, color: "#0f172a", lineHeight: 1.35 }} />,
  h3: (p) => <h3 {...p} style={{ fontSize: 14, fontWeight: 700, marginTop: 20, marginBottom: 10, color: "#334155" }} />,
  ul: (p) => <ul {...p} style={{ paddingLeft: 20, marginBottom: 10 }} />,
  li: (p) => <li {...p} style={{ marginBottom: 6 }} />,
  strong: (p) => <strong {...p} style={{ color: "#0f172a", fontWeight: 700 }} />,
  em: (p) => <em {...p} style={{ color: "#64748b", fontStyle: "normal", fontSize: 12, fontWeight: 600 }} />,
  code: (p) => <code {...p} style={{ background: "#f1f5f9", padding: "1px 6px", borderRadius: 4, fontSize: 12 }} />,
  blockquote: (p) => <blockquote {...p} style={{ borderLeft: "3px solid #c7d2fe", paddingLeft: 12, margin: "8px 0", color: "#475569", fontStyle: "italic" }} />,
};

export default function CoachingDrawer({ open, rep, rangeDays = 7, onClose }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [generatedAt, setGeneratedAt] = useState(null);

  const runCoaching = useCallback(async (force = false) => {
    if (!rep?.agent_id && !rep?.id) return;
    const repId = rep.agent_id || rep.id;
    setLoading(true);
    setError("");
    try {
      // If we already have a report and the caller didn't force a regen,
      // serve from the latest endpoint (fast, no Lumina round-trip).
      if (!force) {
        const latest = await axios.get(`${API}/admin/coaching/rep/${repId}/latest`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token") || ""}` },
        });
        if (latest.data?.report?.markdown) {
          setReport(latest.data.report);
          setGeneratedAt(latest.data.report.created_at);
          setLoading(false);
          return;
        }
      }
      // Otherwise hit the run endpoint (this is the expensive call —
      // typically 15-40s upstream).
      const res = await axios.post(
        `${API}/admin/coaching/rep/${repId}/run?range=${rangeDays}`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token") || ""}` },
          timeout: 95000,
        }
      );
      setReport(res.data);
      setGeneratedAt(res.data?.created_at);
      toast.success("Coaching report ready", { duration: 2200 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to generate coaching report";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Coaching failed: " + (typeof msg === "string" ? msg : "see drawer"));
    } finally {
      setLoading(false);
    }
  }, [rep, rangeDays]);

  useEffect(() => {
    if (open && rep) {
      setReport(null);
      setError("");
      runCoaching(false);
    }
  }, [open, rep, runCoaching]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="coaching-drawer-backdrop"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)",
          backdropFilter: "blur(4px)", zIndex: 1100,
        }}
      />
      {/* Panel */}
      <div
        data-testid="coaching-drawer"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(640px, 100vw)", background: "#ffffff",
          boxShadow: "-20px 0 60px rgba(15,23,42,0.18)",
          zIndex: 1101, display: "flex", flexDirection: "column",
          fontFamily: "inherit",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "18px 22px", borderBottom: "0.5px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Sparkles style={{ width: 16, height: 16, color: "#fff" }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#6366f1", letterSpacing: 0.5, textTransform: "uppercase" }}>
                CCM Coaching · Last {rangeDays} days
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {rep?.agent_name || rep?.full_name || rep?.agent_email || rep?.email || "—"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button
              data-testid="coaching-regen-btn"
              onClick={() => runCoaching(true)}
              disabled={loading}
              title="Regenerate coaching report"
              style={{
                background: "#f1f5f9", color: "#475569", border: "none",
                borderRadius: 8, padding: "6px 10px", cursor: loading ? "wait" : "pointer",
                fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4,
                fontFamily: "inherit", opacity: loading ? 0.5 : 1,
              }}
            >
              <RefreshCcw style={{ width: 11, height: 11 }} /> Regenerate
            </button>
            <button
              data-testid="coaching-drawer-close"
              onClick={onClose}
              style={{
                background: "transparent", border: "none", padding: 6, cursor: "pointer",
                color: "#64748b", borderRadius: 6,
              }}
            >
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>

        {/* Meta line */}
        {generatedAt && !loading && (
          <div style={{ padding: "8px 22px", background: "#f8fafc", fontSize: 10.5, color: "#64748b", borderBottom: "0.5px solid #e2e8f0" }}>
            Generated {new Date(generatedAt).toLocaleString()} · {report?.activity_count ?? 0} activity rows analyzed
          </div>
        )}

        {/* Body */}
        <div data-testid="coaching-drawer-body" style={{ flex: 1, overflowY: "auto", padding: "22px 26px" }}>
          {loading && (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b" }}>
              <div style={{
                width: 36, height: 36, margin: "0 auto 14px",
                border: "2.5px solid #e2e8f0", borderTopColor: "#6366f1",
                borderRadius: "50%", animation: "spin 0.8s linear infinite",
              }} />
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Generating coaching report…</div>
              <div style={{ fontSize: 11.5, color: "#94a3b8" }}>
                Sending activity to Lumina · this typically takes 15-40 seconds
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          {!loading && error && (
            <div data-testid="coaching-error" style={{
              background: "#fef2f2", border: "0.5px solid #fecaca", borderRadius: 10,
              padding: 16, color: "#991b1b", fontSize: 12.5,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Coaching failed</div>
              <div style={{ lineHeight: 1.6 }}>{error}</div>
              <button
                onClick={() => runCoaching(true)}
                style={{
                  marginTop: 12, background: "#dc2626", color: "#fff", border: "none",
                  borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 11, fontWeight: 600,
                  fontFamily: "inherit",
                }}
              >Retry</button>
            </div>
          )}
          {!loading && !error && report?.markdown && (
            <div data-testid="coaching-markdown" style={{
              fontSize: 13.5, lineHeight: 1.65, color: "#1e293b",
            }}>
              <ReactMarkdown components={MD_COMPONENTS}>{report.markdown}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "10px 22px", borderTop: "0.5px solid #e2e8f0",
          fontSize: 10, color: "#94a3b8", display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>Powered by Lumina · Cold Caller Coaching workflow</span>
          <span style={{ color: "#cbd5e1" }}>iter 193</span>
        </div>
      </div>
    </>
  );
}
